import React from 'react';
import { Helmet } from 'react-helmet-async';
import seoData from '../data/seo.json';

const SEO = ({ page = 'home', customTitle, customDescription }) => {
    const pageData = seoData.pages[page] || seoData.pages.home;
    const business = seoData.business;

    const title = customTitle || pageData.title;
    const description = customDescription || pageData.description;
    const keywords = pageData.keywords;

    // LocalBusiness Schema
    const localBusinessSchema = {
        "@context": "https://schema.org",
        "@type": "Plumber",
        "@id": `${business.url}/#organization`,
        "name": business.name,
        "legalName": business.legalName,
        "description": business.description,
        "url": business.url,
        "logo": business.logo,
        "image": business.logo,
        "telephone": business.phone,
        "email": business.email,
        "foundingDate": business.foundingYear,
        "priceRange": business.priceRange,
        "address": {
            "@type": "PostalAddress",
            "streetAddress": business.address.streetAddress,
            "addressLocality": business.address.addressLocality,
            "addressRegion": business.address.addressRegion,
            "postalCode": business.address.postalCode,
            "addressCountry": business.address.addressCountry
        },
        "geo": {
            "@type": "GeoCoordinates",
            "latitude": business.geo.latitude,
            "longitude": business.geo.longitude
        },
        "openingHoursSpecification": [
            {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                "opens": "08:30",
                "closes": "16:30"
            },
            {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Saturday", "Sunday"],
                "opens": "00:00",
                "closes": "23:59",
                "description": "On call for emergencies"
            }
        ],
        "areaServed": business.serviceAreas.map(area => ({
            "@type": "City",
            "name": area
        })),
        "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Plumbing & Heating Services",
            "itemListElement": [
                {
                    "@type": "Offer",
                    "itemOffered": {
                        "@type": "Service",
                        "name": "Emergency Plumbing",
                        "description": "24/7 emergency plumbing services in Calgary"
                    }
                },
                {
                    "@type": "Offer",
                    "itemOffered": {
                        "@type": "Service",
                        "name": "Furnace Repair",
                        "description": "Expert furnace repair and installation services"
                    }
                },
                {
                    "@type": "Offer",
                    "itemOffered": {
                        "@type": "Service",
                        "name": "Drain Cleaning",
                        "description": "Professional drain cleaning and clog removal"
                    }
                },
                {
                    "@type": "Offer",
                    "itemOffered": {
                        "@type": "Service",
                        "name": "Water Heater Repair",
                        "description": "Water heater and hot water tank repair services"
                    }
                },
                {
                    "@type": "Offer",
                    "itemOffered": {
                        "@type": "Service",
                        "name": "Boiler Services",
                        "description": "Boiler repair, installation, and hydronic heating"
                    }
                },
                {
                    "@type": "Offer",
                    "itemOffered": {
                        "@type": "Service",
                        "name": "Gasfitting",
                        "description": "Licensed gas line installation and repair"
                    }
                }
            ]
        },
        "sameAs": [
            business.socialMedia.facebook,
            business.socialMedia.instagram,
            business.socialMedia.googleBusiness
        ].filter(Boolean)
    };

    // FAQ Schema (only on home page)
    const faqSchema = page === 'home' ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": seoData.faqs.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
            }
        }))
    } : null;

    // Breadcrumb Schema
    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
            {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": business.url
            },
            ...(page !== 'home' ? [{
                "@type": "ListItem",
                "position": 2,
                "name": pageData.title.split('|')[0].trim(),
                "item": `${business.url}/${page}`
            }] : [])
        ]
    };

    return (
        <Helmet>
            {/* Primary Meta Tags */}
            <title>{title}</title>
            <meta name="title" content={title} />
            <meta name="description" content={description} />
            <meta name="keywords" content={keywords} />
            <meta name="author" content={business.name} />
            <meta name="robots" content="index, follow" />
            <link rel="canonical" href={`${business.url}${page === 'home' ? '' : `/${page}`}`} />

            {/* Open Graph / Facebook */}
            <meta property="og:type" content="website" />
            <meta property="og:url" content={`${business.url}${page === 'home' ? '' : `/${page}`}`} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:site_name" content={business.name} />
            <meta property="og:locale" content="en_CA" />

            {/* Geo Tags for Local SEO */}
            <meta name="geo.region" content="CA-AB" />
            <meta name="geo.placename" content="Calgary" />
            <meta name="geo.position" content={`${business.geo.latitude};${business.geo.longitude}`} />
            <meta name="ICBM" content={`${business.geo.latitude}, ${business.geo.longitude}`} />

            {/* Schema.org JSON-LD */}
            <script type="application/ld+json">
                {JSON.stringify(localBusinessSchema)}
            </script>
            <script type="application/ld+json">
                {JSON.stringify(breadcrumbSchema)}
            </script>
            {faqSchema && (
                <script type="application/ld+json">
                    {JSON.stringify(faqSchema)}
                </script>
            )}
        </Helmet>
    );
};

export default SEO;

